/**
 * Shared timeline types for the unified agent feed.
 */

import type { MessageDoc } from "@convex-dev/agent";

export interface SubAgentInfo {
  id: string;
  label: string;
  threadId: string;
  status: string;
  startedAt: number;
  completedAt?: number;
  tokensUsed?: { input: number; output: number };
  entityCount?: number;
  error?: string;
  lastHeartbeat?: number;
}

export type TimelineEntry =
  | {
      type: "activity";
      timestamp: number;
      message: string;
      key: string;
    }
  | {
      type: "agent_message";
      timestamp: number;
      source: string;
      sourceLabel: string;
      message: MessageDoc;
      role: string;
      key: string;
      streaming: boolean;
    }
  | {
      type: "lifecycle";
      timestamp: number;
      subAgentId: string;
      event: "started" | "completed" | "failed";
      label: string;
      entityCount?: number;
      error?: string;
      key: string;
    };

export type GroupedTimelineItem =
  | { kind: "single"; entry: TimelineEntry }
  | {
      kind: "message_group";
      source: string;
      sourceLabel: string;
      entries: Extract<TimelineEntry, { type: "agent_message" }>[];
      firstTimestamp: number;
      lastTimestamp: number;
      hasStreaming: boolean;
      key: string;
    }
  | {
      kind: "tool_pair";
      agent: string;
      toolName: string;
      callTimestamp: number;
      resultTimestamp?: number;
      status: "pending" | "completed";
      key: string;
    };

/**
 * TaskLoop worker node shape, as consumed here. The backing field mirrors an
 * agent-authored task tree with no fixed schema; this is the subset of
 * fields the timeline reads off each node.
 */
export interface TaskTreeNode {
  type?: string;
  id: string;
  threadId?: string;
  description?: string;
  status?: string;
  startedAt?: number;
  dispatchedAt?: number;
  completedAt?: number;
  error?: string;
}

/**
 * Reads `taskTree` off a summarization task's `workingMemory` field. The
 * backend query (`getSummarizationTaskDetail`) declares `workingMemory` as
 * `v.any()` — its shape varies per agent — so this narrows only the
 * `taskTree` field the unified timeline consumes.
 */
export function getTaskTree(
  workingMemory: unknown,
): TaskTreeNode[] | undefined {
  if (typeof workingMemory !== "object" || workingMemory === null) {
    return undefined;
  }
  const { taskTree } = workingMemory as { taskTree?: unknown };
  return Array.isArray(taskTree) ? (taskTree as TaskTreeNode[]) : undefined;
}

export interface TimelineProps {
  task: {
    threadId?: string;
    jobId: string;
    status: string;
    activityLog?: Array<{ timestamp: number; message: string }>;
  };
  subAgents: SubAgentInfo[];
  taskTree?: TaskTreeNode[]; // TaskLoop worker nodes with threadId
  isRunning: boolean;
}
